import { useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Tooltip, Box, SxProps, Theme, Select, MenuItem, Typography } from '@mui/material';

import { ProfileChip } from './ProfileChip';
import { ProfileSelectXClear } from './ProfileSelectXClear';
import { OptionType, BaseFormFieldProps } from './types';

export const ProfileFormSelect = ({
  isMultiple,
  field,
  options,
  controllerField,
  groupedFields,
  index,
  groupKey,
  chipType,
  showSearch,
  textLeadingAdornment,
  disabled,
  showHoverDelete = true,
}: BaseFormFieldProps & {
  options: OptionType[];
}) => {
  const [isHovering, setIsHovering] = useState(false);

  const selectedOptions = isMultiple
    ? Array.isArray(controllerField.value)
      ? controllerField.value.map((value) => {
          const option = options.find((opt) => opt.value === value);

          return option || { label: value, value };
        })
      : []
    : controllerField.value || '';

  const textLeadingStartAdornment = (name: string) => {
    const content = (
      <Typography
        variant="text-md-regular"
        color={(theme) => theme.backrs.colors['Gray/600']}
        sx={textLeadingSx}
      >
        {name}
      </Typography>
    );

    return (
      <Box sx={startAdornmentContainerSx}>
        {name.length > 20 && (
          <Tooltip title={name} arrow placement="top">
            {content}
          </Tooltip>
        )}

        {name.length <= 20 && content}
      </Box>
    );
  };

  return (
    <>
      {textLeadingAdornment?.name && (
        <Typography variant="text-sm-regular" sx={labelSmallScreenSx}>
          {textLeadingAdornment.name}
        </Typography>
      )}
      <Select
        disabled={disabled}
        displayEmpty
        multiple={isMultiple}
        value={controllerField.value || ''}
        placeholder={field.placeholder ?? ''}
        inputProps={{ 'aria-label': 'Without label' }}
        IconComponent={(props) => (
          <Box
            className={props.className}
            sx={chevronIconContainerSx(props.className.includes('MuiSelect-iconOpen'))}
          >
            <AppIcon iconName="ChevronDown" diameter="20px" color="Gray/400" />
          </Box>
        )}
        startAdornment={
          textLeadingAdornment?.name ? (
            textLeadingStartAdornment(textLeadingAdornment.name)
          ) : showSearch ? (
            <Box sx={searchIconSx}>
              <AppIcon diameter="24px" iconName="SearchMd" color="Gray/500" />
            </Box>
          ) : null
        }
        onChange={(event) => {
          const value = event.target.value;

          const newValue = Array.isArray(value)
            ? Array.from(
                new Set(
                  value.map((option) => (typeof option === 'string' ? option : option.value)),
                ),
              )
            : (typeof value === 'string' ? value : value?.value) || '';

          controllerField.onChange(newValue);
        }}
        renderValue={(selected) => {
          const shouldShowPlaceholder =
            selectedOptions.length === 0 || isMultiple || typeof selected !== 'string';

          if (shouldShowPlaceholder) {
            return (
              <Typography
                color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
              >
                {field.placeholder}
              </Typography>
            );
          }

          const option = options.find((opt) => opt.value === selected);

          if (!showHoverDelete || !option) {
            return <Typography>{option?.label || selected}</Typography>;
          }

          return (
            <ProfileSelectXClear
              disabled={disabled}
              isHovering={isHovering}
              setIsHovering={setIsHovering}
              label={option?.label || selected}
              onDelete={() => {
                controllerField.onChange(null);
                setIsHovering(false);
              }}
            />
          );
        }}
        sx={selectTextFieldStyle(index === Object.entries(groupedFields).length - 1)}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {options.map((option) => {
          const isSelected =
            isMultiple && Array.isArray(controllerField.value)
              ? controllerField.value.includes(option.value)
              : controllerField.value === option.value;

          return (
            <MenuItem sx={menuItemSx} key={option.value} value={option.value}>
              {option.label}
              {isSelected && (
                <AppIcon
                  iconName="Check"
                  diameter="20px"
                  color="Colors/Foreground/fg-brand-primary"
                />
              )}
            </MenuItem>
          );
        })}
      </Select>
      {isMultiple && Array.isArray(selectedOptions) && selectedOptions.length > 0 && (
        <Box sx={underneathTagContainerSx}>
          {selectedOptions.map((option, index) => (
            <ProfileChip
              key={`${groupKey}-${field.name}-${option.value}-${index}`}
              variant={chipType ?? 'tag'}
              option={option}
              onDelete={() => {
                const currentValues = Array.isArray(controllerField.value)
                  ? controllerField.value
                  : [];
                const updatedValue = currentValues.filter((v: string) => v !== option.value);

                controllerField.onChange(updatedValue);
              }}
              groupKey={groupKey}
              fieldName={field.name || ''}
              index={index}
            />
          ))}
        </Box>
      )}
    </>
  );
};

const underneathTagContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
  mb: backrs.spacing['spacing-lg'],
});

const startAdornmentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  alignItems: 'center',
  paddingRight: backrs.spacing['spacing-md'],
  maxWidth: { xs: '50%', md: '175px' },
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  maxHeight: 'unset',
  height: backrs.spacing['spacing-5xl'],
  display: {
    xs: 'none',
    sm: 'flex',
  },
});

const textLeadingSx: SxProps<Theme> = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  width: '100%',
};

const selectTextFieldStyle =
  (isLastItem: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    height: '44px', // to match the height of the text input fields
    border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    mb: {
      xs: backrs.spacing['spacing-md'],
      sm: backrs.spacing[isLastItem ? 'spacing-md' : 'spacing-2xl'],
    },
    width: '100%',
    paddingLeft: backrs.radius['radius-lg'],
    '& .MuiSelect-icon': {
      display: 'block',
    },
    '&.Mui-disabled': {
      backgroundColor: backrs.colors['Colors/Background/bg-disabled-subtle'],
      borderColor: backrs.colors['Colors/Border/border-disabled'],
      boxShadow: backrs.shadows['shadow-xs'],
    },

    '& .MuiSelect-select': {
      paddingLeft: `${backrs.spacing['spacing-none']} !important`,
    },
  });

const chevronIconContainerSx = (isOpen: boolean): SxProps<Theme> => ({
  display: 'flex',
  padding: '0px 5px',
  transition: 'all 0.3s ease-in-out',
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  bottom: isOpen ? '10px' : '0px',
});

const searchIconSx: SxProps<Theme> = ({ backrs }) => ({
  paddingX: backrs.spacing['spacing-xs'],
});

const labelSmallScreenSx: SxProps<Theme> = ({ backrs }) => ({
  display: {
    xs: 'block',
    sm: 'none',
  },
  my: {
    xs: backrs.spacing['spacing-xs'],
    sm: backrs.spacing['spacing-none'],
  },
});

const menuItemSx: SxProps<Theme> = ({ backrs }) => ({
  transition: 'all 0.3s ease-in-out',
  display: 'flex',
  justifyContent: 'space-between',
  '&.MuiMenuItem-root': {
    '&:hover': {
      backgroundColor: `${backrs.colors['Colors/Background/bg-active']} !important`,
    },
  },
  '&.Mui-focusVisible': {
    backgroundColor: `${backrs.colors['Colors/Background/bg-active']} !important`,
  },
  '& .MuiCheckbox-root': {
    pointerEvents: 'none',
  },

  '&.Mui-selected': {
    backgroundColor: `${backrs.colors['Colors/Background/bg-active']} !important`,
  },
  '&.Mui-selected:hover': {
    backgroundColor: `${backrs.colors['Colors/Background/bg-active']} !important`,
  },
  '&.Mui-selected.Mui-focusVisible': {
    backgroundColor: `${backrs.colors['Colors/Background/bg-active']} !important`,
  },
});
